'use client'

import { HugeiconsIcon } from '@hugeicons/react'
import { AiBrain01Icon, CancelCircleIcon, CheckmarkCircle01Icon, CodeIcon, InformationCircleIcon } from '@hugeicons-pro/core-solid-rounded'
import { cn } from '@/lib/utils'
import type { TraceStep as TraceStepData } from '@/lib/commands/trace'

export function TraceStep({ step, index }: { step: TraceStepData; index: number }) {
  const meta = stepMeta(step)
  return (
    <div className="rounded-lg border border-atrium-border bg-white p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-atrium-content text-atrium-black">
            <HugeiconsIcon icon={meta.icon} className="size-4" />
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs tabular-nums text-atrium-black/45">{index + 1}.</span>
              <span className="truncate text-sm font-medium text-atrium-black">{meta.title}</span>
              {meta.badge && (
                <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-normal', meta.badgeClass)}>
                  {meta.badge}
                </span>
              )}
            </div>
            {meta.subtitle && <p className="mt-1 text-xs text-atrium-black/60">{meta.subtitle}</p>}
          </div>
        </div>
        <span className="shrink-0 text-xs tabular-nums text-atrium-black/50">{formatDuration(meta.durationMs)}</span>
      </div>

      {renderDetails(step)}
    </div>
  )
}

function stepMeta(step: TraceStepData) {
  if (step.kind === 'ai') {
    return {
      icon: AiBrain01Icon,
      title: 'Ask AI',
      subtitle: `${step.model} · ${step.tokensIn} in / ${step.tokensOut} out · ${formatDollars(step.costCents)}`,
      durationMs: step.durationMs,
      badge: 'ok',
      badgeClass: 'bg-green-50 text-green-700',
    }
  }
  if (step.kind === 'dryrun') {
    return {
      icon: CancelCircleIcon,
      title: step.name,
      subtitle: step.description,
      durationMs: step.durationMs ?? 0,
      badge: 'intercepted',
      badgeClass: 'bg-amber-50 text-amber-800',
    }
  }
  if (step.kind === 'tool') {
    return {
      icon: CodeIcon,
      title: step.name,
      subtitle: step.ok ? 'Tool call completed' : step.error,
      durationMs: step.durationMs,
      badge: step.ok ? 'ok' : 'error',
      badgeClass: step.ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700',
    }
  }
  if (step.kind === 'graph_node') {
    return {
      icon: InformationCircleIcon,
      title: `${step.nodeKind} · ${step.nodeId}`,
      subtitle: step.ok ? 'Graph node completed' : step.error,
      durationMs: step.durationMs,
      badge: step.ok ? 'ok' : 'error',
      badgeClass: step.ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700',
    }
  }
  if (step.kind === 'rate_limited') {
    return {
      icon: CancelCircleIcon,
      title: 'Rate limited',
      subtitle: `${step.used}/${step.limit} runs in the last ${step.bucket}; retry in ${Math.max(1, Math.ceil(step.retryAfterSeconds / 60))} minutes`,
      durationMs: 0,
      badge: 'blocked',
      badgeClass: 'bg-amber-50 text-amber-800',
    }
  }
  return {
    icon: CheckmarkCircle01Icon,
    title: 'End',
    subtitle: 'Run completed',
    durationMs: step.durationMs,
    badge: 'ok',
    badgeClass: 'bg-green-50 text-green-700',
  }
}

function renderDetails(step: TraceStepData) {
  if (step.kind === 'ai') {
    return (
      <details className="mt-3">
        <summary className="cursor-pointer text-xs text-atrium-black/60">view prompt</summary>
        <pre className="mt-2 max-h-56 overflow-auto rounded-md bg-atrium-content p-3 text-xs text-atrium-black">{step.preview}</pre>
      </details>
    )
  }
  if (step.kind === 'tool' || step.kind === 'dryrun') {
    const payload = step.kind === 'tool'
      ? { args: step.args, result: step.ok ? step.result : undefined, error: step.ok ? undefined : step.error }
      : { args: step.args, description: step.description, preview: step.preview }
    const download = step.kind === 'tool' && step.ok ? downloadFromResult(step.result) : null
    return (
      <div className="mt-3 space-y-2">
        {download ? (
          <a
            href={download.url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex text-xs font-medium text-accent-600 hover:underline"
          >
            Download {download.filename}
          </a>
        ) : null}
        <details>
          <summary className="cursor-pointer text-xs text-atrium-black/60">view JSON</summary>
          <pre className="mt-2 max-h-56 overflow-auto rounded-md bg-atrium-content p-3 text-xs text-atrium-black">
            {JSON.stringify(payload, null, 2)}
          </pre>
        </details>
      </div>
    )
  }
  if (step.kind === 'graph_node' && step.error) {
    return <p className="mt-2 text-xs text-red-700">{step.error}</p>
  }
  return null
}

function downloadFromResult(result: unknown): { url: string; filename: string } | null {
  if (!result || typeof result !== 'object' || Array.isArray(result)) return null
  const record = result as Record<string, unknown>
  if (typeof record.download_url !== 'string' || !record.download_url) return null
  const filename = typeof record.filename === 'string' && record.filename ? record.filename : 'file'
  return { url: record.download_url, filename }
}

function formatDuration(ms: number) {
  return `${Math.max(0, Math.round(ms))}ms`
}

function formatDollars(cents: number) {
  return `$${(cents / 100).toFixed(3)}`
}
